import { AppManual } from "./app-manual";
import { MoreMenuIcon } from "@taunggyi-plus/design-system";
import { useOrderActivity } from "./order-activity";
import { LinkWordmark } from "@taunggyi-plus/design-system";
import { SignOutButton, useFeedback } from "@taunggyi-plus/auth";
import {
  CustomerSavedRecords,
  WorkspaceDashboard,
  WorkspaceOrderCounts,
  ManagedRiders,
  TaxonomySettings,
  ManagedContent,
  StaffWorkspace,
  ServiceCare,
  EventsWorkspace,
  TaxiSubscriptionRequests,
  PartnerPromotionRequests,
  ProviderSettings,
} from "@taunggyi-plus/finance-feature";
import { InstallApp } from "./install-app";
import {
  Component,
  cloneElement,
  isValidElement,
  type ReactElement,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent,
  type ReactNode,
} from "react";

import { useAuth } from "@taunggyi-plus/auth";
import { platformOwner, type AppConfig } from "@taunggyi-plus/config";
import { BrandLogo, Card, PageTitle, GlobeIcon } from "@taunggyi-plus/design-system";
import type { Locale } from "@taunggyi-plus/domain";
import { supportedLocales, translate, translateApp } from "@taunggyi-plus/i18n";

const localeLabels: Record<Locale, string> = {
  en: "English",
  my: "မြန်မာ",
  shn: "တႆး",
  zh: "中文",
};

type InformationId = "about" | "terms" | "privacy" | "cancellation" | "support";

const preferenceCopy: Record<
  Locale,
  {
    title: string;
    language: string;
    close: string;
  }
> = {
  en: {
    title: "Language",
    language: "Language",
    close: "Close language settings",
  },
  my: {
    title: "ဘာသာစကား",
    language: "ဘာသာစကား",
    close: "နှစ်သက်ရာများ ပိတ်မည်",
  },
  shn: {
    title: "ၽႃႇသႃႇ",
    language: "ၽႃႇသႃႇ",
    close: "ပိၵ်ႉလွင်ႈလိူၵ်ႈၸႂ်",
  },
  zh: {
    title: "语言",
    language: "语言",
    close: "关闭语言选择",
  },
};

const footerCopy: Record<
  Locale,
  {
    hotline: string;
    company: string;
    legal: string;
    about: string;
    contact: string;
    terms: string;
    privacy: string;
    cancellation: string;
    community: string;
  }
> = {
  en: {
    hotline: "Hotline",
    company: "Company",
    legal: "Legal & safety",
    about: "About us",
    contact: "Contact & support",
    terms: "Terms & conditions",
    privacy: "Privacy policy",
    cancellation: "Cancellation policy",
    community: "Built for the Taunggyi community.",
  },
  my: {
    hotline: "ဟော့လိုင်း",
    company: "အဖွဲ့အစည်း",
    legal: "ဥပဒေနှင့် လုံခြုံရေး",
    about: "ကျွန်ုပ်တို့အကြောင်း",
    contact: "ဆက်သွယ်ရန်နှင့် အကူအညီ",
    terms: "စည်းကမ်းချက်များ",
    privacy: "ကိုယ်ရေးအချက်အလက် မူဝါဒ",
    cancellation: "ပယ်ဖျက်ခြင်း မူဝါဒ",
    community: "တောင်ကြီးအသိုင်းအဝိုင်းအတွက် တည်ဆောက်ထားသည်။",
  },
  shn: {
    hotline: "Hotline",
    company: "မုၵ်ႉၸုမ်း",
    legal: "မၢႆမီႈလႄႈလွင်ႈႁူမ်ႇလူမ်ႈ",
    about: "လွင်ႈႁဝ်းၶႃႈ",
    contact: "ၵပ်းသိုပ်ႇလႄႈၸွႆႈထႅမ်",
    terms: "ၶေႃႈမၵ်းမၼ်ႈ",
    privacy: "ပေႃႇလသီႇသုၼ်ႇတူဝ်",
    cancellation: "ပေႃႇလသီႇယုၵ်ႉလိူၵ်ႈ",
    community: "ႁဵတ်းဝႆႉတႃႇတူင်ႇဝူင်းတူၼ်ႈတီး။",
  },
  zh: {
    hotline: "热线",
    company: "组织",
    legal: "法律与安全",
    about: "关于我们",
    contact: "联系与支持",
    terms: "条款与条件",
    privacy: "隐私政策",
    cancellation: "取消政策",
    community: "为东枝社区而打造。",
  },
};

const defaultEventCopy: Record<Locale, { intro: string; title: string; description: string }> = {
  en: {
    intro: "Discover important festivals and community events across Taunggyi.",
    title: "Taunggyi Tazaungdaing Festival",
    description: "Official schedules and verified organizer information will appear here.",
  },
  my: {
    intro: "တောင်ကြီးမြို့ရှိ အရေးကြီးပွဲတော်နှင့် လူမှုအသိုင်းအဝိုင်းပွဲများကို ရှာဖွေပါ။",
    title: "တောင်ကြီး တန်ဆောင်တိုင်ပွဲတော်",
    description: "တရားဝင်အစီအစဉ်နှင့် အတည်ပြုစီစဉ်သူအချက်အလက်များကို ဤနေရာတွင် ဖော်ပြပါမည်။",
  },
  shn: {
    intro: "ႁႃပွႆးလႄႈၵၢၼ်တူင်ႇဝူင်းဢၼ်လမ်ႇလွင်ႈၼႂ်းတူၼ်ႈတီး။",
    title: "ပွႆးတၼ်းသွင်တႅင်ႇ တူၼ်ႈတီး",
    description: "တေၼႄသဵၼ်ႈၵၢၼ်တၢင်းၵၢၼ်လႄႈၶေႃႈမုၼ်းၽူႈၸတ်းဢၼ်ယိုၼ်ယၼ်ဝႆႉ။",
  },
  zh: {
    intro: "探索东枝的重要节庆和社区活动。",
    title: "东枝德桑岱节",
    description: "官方日程和经过认证的主办方信息将在此公布。",
  },
};

type AppRoute =
  | "home"
  | "search"
  | "services"
  | "notifications"
  | "events"
  | "dashboard"
  | "my-services"
  | "orders"
  | "finance"
  | "more"
  | "not-found"
  | "server-error";

const routePaths: Record<AppRoute, string> = {
  home: "/",
  search: "/search",
  services: "/services",
  notifications: "/notifications",
  events: "/events",
  dashboard: "/dashboard",
  "my-services": "/my-services",
  orders: "/orders",
  finance: "/finance",
  more: "/more",
  "not-found": "/404",
  "server-error": "/500",
};

function routeFromPath(pathname: string, customer = false): AppRoute {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/search") return customer ? "search" : "not-found";
  if (path === "/services") return "services";
  if (path === "/notifications") return "notifications";
  if (path === "/events" || path.startsWith("/events/")) return "events";
  if (path === "/dashboard") return "dashboard";
  if (["/my-services", "/drivers", "/rooms", "/foods", "/riders", "/partners"].includes(path))
    return "my-services";
  if (path === "/orders") return "orders";
  if (path === "/finance") return "finance";
  if (path === "/profile" || path === "/more" || path.startsWith("/more/")) return "more";
  if (path === "/500") return "server-error";
  if (path === "/404") return "not-found";
  return path === "/" ? "home" : "not-found";
}

function moreTitle(pathname: string, locale: Locale) {
  const path = pathname.replace(/\/+$/, "") || "/more";
  const titles: Record<string, string> = {
    "/more/profile": "Profile",
    "/more/favourites": "Favourites",
    "/more/reviews": "Reviews",
    "/more/categories": "Categories & tags",
    "/more/rooms": "Rooms",
    "/more/products": "Products",
    "/more/riders": "Rider accounts",
    "/more/taxi-data": "Taxi data",
    "/more/customers": "Customers",
    "/more/custom-notifications": "Custom notifications",
    "/more/support": "Support",
    "/more/legal": "Legal",
    "/more/safety": "Safety",
    "/more/subscription": "Subscription fee",
    "/more/language": preferenceCopy[locale].title,
    "/more/legal-support": "Legal, safety and support",
    "/more/how-to-use": "How to use Link",
    "/more/how-to-use-taxi": "How to use the taxi app",
    "/more/audit": "Audit",
    "/more/events": "Events",
    "/more/services": "Services",
    "/more/approvals": "Partner approvals",
    "/more/subscription-requests": "Subscription requests",
    "/more/promotions": "Promotions",
    "/more/subscriptions": "Subscriptions",
    "/more/operations": "Service work",
    "/more/work-thresholds": "Overdue thresholds",
    "/more/taxi-leaders": "Taxi Driver List",
    "/more/taxi-drivers": "Taxi Driver List",
    "/more/hotels": "Hotel List",
    "/more/merchants": "Merchant List",
    "/more/delivery-owners": "Delivery Owner List",
    "/more/delivery-riders": "Delivery Rider List",
    "/more/backup": "Database backup",
    "/more/team": "Admin team permissions",
    "/more/ad-packages": "Advertising packages & results",
    "/more/provider-settings": "Opening hours & availability",
    "/more/service-help": "Order support, refunds & reviews",
    "/more/provider-events": "Event participation",
    "/more/provider-subscription": "Subscription fee",
    "/more/provider-ads": "Promotions",
  };
  return titles[path] ?? translate(locale, "more");
}

interface PlatformAppProps {
  readonly config: AppConfig;
  readonly navigationMode?: "public" | "owner";
  readonly compactNavigation?: boolean;
  readonly homeContent?: ReactNode | ((locale: Locale) => ReactNode);
  readonly searchContent?: ReactNode | ((locale: Locale) => ReactNode);
  readonly featureContent?: ReactNode | ((locale: Locale) => ReactNode);
  readonly notificationContent?: ReactNode | ((locale: Locale) => ReactNode);
  readonly eventContent?: ReactNode | ((locale: Locale) => ReactNode);
  readonly moreContent?:
    | ReactNode
    | ((
        locale: Locale,
        onLocaleChange: (locale: Locale) => void,
        onOpenPreferences: () => void,
      ) => ReactNode);
  readonly dashboardContent?: ReactNode | ((locale: Locale) => ReactNode);
  readonly myServicesContent?: ReactNode | ((locale: Locale) => ReactNode);
  readonly myServicesLabel?: string | ((locale: Locale) => string);
  readonly myServicesPath?:
    "/my-services" | "/drivers" | "/rooms" | "/foods" | "/riders" | "/partners";
  readonly ordersContent?: ReactNode | ((locale: Locale) => ReactNode);
  readonly financeContent?: ReactNode | ((locale: Locale) => ReactNode);
  readonly financeLabel?: string | ((locale: Locale) => string);
  readonly contentOwnsHeading?: readonly AppRoute[];
  readonly moreContentOwnsHeadingPaths?: readonly string[];
  readonly informationContent?: (
    locale: Locale,
    id: InformationId,
    onClose: () => void,
  ) => ReactNode;
}

export function PlatformApp(props: PlatformAppProps) {
  return (
    <PlatformErrorBoundary config={props.config}>
      <PlatformAppContent {...props} />
    </PlatformErrorBoundary>
  );
}

function PlatformAppContent({
  config,
  navigationMode = "public",
  compactNavigation: _compactNavigation = false,
  homeContent,
  searchContent,
  featureContent,
  notificationContent,
  eventContent,
  moreContent,
  dashboardContent: _dashboardContent,
  myServicesContent,
  myServicesLabel,
  myServicesPath = "/my-services",
  ordersContent,
  financeContent,
  financeLabel: _financeLabel,
  contentOwnsHeading = [],
  moreContentOwnsHeadingPaths = [],
  informationContent,
}: PlatformAppProps) {
  const auth = useAuth();
  const orderActivity = useOrderActivity();
  const [capabilities, setCapabilities] = useState<string[]>([]);
  useEffect(() => {
    if (config.id !== "admin" || !auth.client || auth.loading || !auth.user) return;
    let active = true;
    void auth.client.rpc("my_admin_capabilities").then((r) => {
      if (active && !r.error) setCapabilities((r.data ?? []) as string[]);
    });
    return () => {
      active = false;
    };
  }, [auth.client, auth.loading, auth.user, config.id]);
  const scopedStaff = capabilities.length > 0 && !capabilities.includes("all");

  const ownerNavigation = navigationMode === "owner";
  const [locale, setLocale] = useState<Locale>(() => {
    const saved =
      localStorage.getItem("taunggyi-link-locale") ?? localStorage.getItem("tg-plus-locale");
    return supportedLocales.includes(saved as Locale) ? (saved as Locale) : "en";
  });
  const footer = footerCopy[locale];
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [selectedInformation, setSelectedInformation] = useState<InformationId | null>(null);
  const [route, setRoute] = useState<AppRoute>(() => {
    const initialRoute = routeFromPath(window.location.pathname, config.id === "customer");
    return ownerNavigation && initialRoute === "home" ? "dashboard" : initialRoute;
  });
  const [locationVersion, setLocationVersion] = useState(0);

  useEffect(() => {
    document.documentElement.lang = locale;
    localStorage.setItem("taunggyi-link-locale", locale);
  }, [locale]);

  useEffect(() => {
    document.documentElement.dataset.theme = "dark";
    document.documentElement.style.colorScheme = "dark";
    localStorage.setItem("taunggyi-link-theme", "dark");
  }, []);

  useEffect(() => {
    if (ownerNavigation && window.location.pathname === "/") {
      window.history.replaceState({}, "", "/dashboard");
    }
    if (window.location.pathname === "/profile") {
      window.history.replaceState({}, "", "/more/profile");
    }
    const handlePopState = () => {
      setRoute(routeFromPath(window.location.pathname, config.id === "customer"));
      setLocationVersion((value) => value + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [config.id, ownerNavigation]);

  function navigate(nextRoute: AppRoute, event: MouseEvent<HTMLAnchorElement>) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    const nextPath = new URL(event.currentTarget.href, window.location.href).pathname;
    if (window.location.pathname !== nextPath) window.history.pushState({}, "", nextPath);
    setRoute(nextRoute);
    setLocationVersion((value) => value + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function navigateInternalLink(event: MouseEvent<HTMLDivElement>) {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest("a[href]");
    if (!(anchor instanceof HTMLAnchorElement) || anchor.target || anchor.hasAttribute("download"))
      return;
    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin || url.protocol !== window.location.protocol) return;
    const nextRoute = routeFromPath(url.pathname, config.id === "customer");
    if (nextRoute === "not-found" || url.hash) return;
    event.preventDefault();
    const nextLocation = `${url.pathname}${url.search}${url.hash}`;
    if (
      `${window.location.pathname}${window.location.search}${window.location.hash}` !== nextLocation
    )
      window.history.pushState({}, "", nextLocation);
    setRoute(nextRoute);
    setLocationVersion((value) => value + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function openInformation(id: InformationId, event: MouseEvent<HTMLAnchorElement>) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    setSelectedInformation(id);
  }

  const workspaceLabel = translate(locale, "services");
  const resolvedMyServicesLabel =
    typeof myServicesLabel === "function"
      ? myServicesLabel(locale)
      : (myServicesLabel ?? translate(locale, "myServices"));
  const resolvedFinanceLabel = translate(locale, "finance");
  const ordersLabel = translate(locale, "orders");
  const resolvedHome = typeof homeContent === "function" ? homeContent(locale) : homeContent;
  const resolvedSearch =
    typeof searchContent === "function" ? searchContent(locale) : searchContent;
  const resolvedFeature =
    typeof featureContent === "function" ? featureContent(locale) : featureContent;
  const resolvedNotifications =
    typeof notificationContent === "function" ? notificationContent(locale) : notificationContent;
  const resolvedEvents = typeof eventContent === "function" ? eventContent(locale) : eventContent;
  const resolvedMore =
    typeof moreContent === "function"
      ? moreContent(locale, setLocale, () => setPreferencesOpen(true))
      : moreContent;
  const resolvedMyServices =
    typeof myServicesContent === "function" ? myServicesContent(locale) : myServicesContent;
  const resolvedOrders =
    typeof ordersContent === "function" ? ordersContent(locale) : ordersContent;
  const resolvedFinance =
    typeof financeContent === "function" ? financeContent(locale) : financeContent;
  const morePageTitle = moreTitle(window.location.pathname, locale);
  const roleHomeRoute: AppRoute = ownerNavigation ? "dashboard" : "home";
  const roleHomePath = ownerNavigation ? routePaths.dashboard : routePaths.home;
  const topActionRoute: AppRoute = config.id === "customer" ? "events" : "notifications";
  const topActionLabel = translate(locale, config.id === "customer" ? "events" : "notifications");

  if (window.location.pathname === "/reset-password") {
    return (
      <AuthenticationScreen config={config} locale={locale} onLocaleChange={setLocale}>
        <PasswordRecovery locale={locale} reset />
      </AuthenticationScreen>
    );
  }

  if (auth.loading) {
    return (
      <AuthenticationScreen config={config} locale={locale} onLocaleChange={setLocale}>
        <Card className="protected-state protected-state--loading">
          <span className="protected-state__spinner" aria-hidden="true" />
          <strong>{translate(locale, "authLoading")}</strong>
        </Card>
      </AuthenticationScreen>
    );
  }

  if (!auth.user) {
    return (
      <AuthenticationScreen config={config} locale={locale} onLocaleChange={setLocale}>
        <ProtectedLogin locale={locale} allowRegistration={config.id === "customer"} />
      </AuthenticationScreen>
    );
  }

  return (
    <div
      className="app-shell authenticated-shell"
      data-app={config.id}
      onClick={navigateInternalLink}
      style={
        { "--accent": config.accent, "--accent-text": config.accentText } as React.CSSProperties
      }
    >
      <a className="skip-link" href="#main-content">
        {translate(locale, "skip")}
      </a>

      <header className="topbar">
        <div className="topbar__inner">
          <a
            className="brand-lockup"
            href={roleHomePath}
            onClick={(event) => navigate(roleHomeRoute, event)}
          >
            <BrandLogo />
            <span className="role-label">
              {translateApp(locale, config.id, "role", config.roleLabel)}
            </span>
          </a>
          <nav className="desktop-nav" aria-label={translate(locale, "workspace")}>
            {ownerNavigation ? (
              <>
                <DesktopLink
                  active={route === "dashboard"}
                  route="dashboard"
                  label={translate(locale, "dashboard")}
                  onNavigate={navigate}
                />
                {resolvedMyServices && config.id === "admin" && (
                  <DesktopLink
                    active={route === "my-services"}
                    route="my-services"
                    href={myServicesPath}
                    label={resolvedMyServicesLabel}
                    onNavigate={navigate}
                  />
                )}
                <DesktopLink
                  active={route === "orders"}
                  route="orders"
                  label={ordersLabel}
                  onNavigate={navigate}
                />
                {resolvedFinance && (
                  <DesktopLink
                    active={route === "finance"}
                    route="finance"
                    label={resolvedFinanceLabel}
                    onNavigate={navigate}
                  />
                )}
                <DesktopLink
                  active={route === "more"}
                  route="more"
                  label={translate(locale, "more")}
                  onNavigate={navigate}
                />
              </>
            ) : (
              <>
                <DesktopLink
                  active={route === "home"}
                  route="home"
                  label={translate(locale, "home")}
                  onNavigate={navigate}
                />
                <DesktopLink
                  active={route === "services"}
                  route="services"
                  label={workspaceLabel}
                  onNavigate={navigate}
                />
                <DesktopLink
                  active={route === "events"}
                  route="events"
                  label={translate(locale, "events")}
                  onNavigate={navigate}
                />
                {resolvedOrders && (
                  <DesktopLink
                    active={route === "orders"}
                    route="orders"
                    label={ordersLabel}
                    onNavigate={navigate}
                  />
                )}
                <DesktopLink
                  active={route === "more"}
                  route="more"
                  label={translate(locale, "more")}
                  onNavigate={navigate}
                />
              </>
            )}
          </nav>
          <div className="topbar__actions">
            {config.id === "customer" && (
              <a
                className="notification-button"
                href={routePaths.search}
                aria-label={translate(locale, "search")}
                aria-current={route === "search" ? "page" : undefined}
                onClick={(event) => navigate("search", event)}
              >
                <HeaderActionGlyph icon="search" />
              </a>
            )}
            {resolvedNotifications &&
              (isValidElement(resolvedNotifications)
                ? cloneElement(resolvedNotifications as ReactElement<{ embedded?: boolean }>, {
                    embedded: false,
                  })
                : resolvedNotifications)}
            {!resolvedNotifications && (
              <a
                className="notification-button"
                href={routePaths[topActionRoute]}
                aria-label={topActionLabel}
                onClick={(event) => navigate(topActionRoute, event)}
              >
                <MobileTabGlyph icon="bell" />
                {orderActivity.unread > 0 && (
                  <span
                    className="order-unread-badge"
                    aria-label={`${orderActivity.unread} unread notifications`}
                  >
                    {orderActivity.unread > 99 ? "99+" : orderActivity.unread}
                  </span>
                )}
              </a>
            )}
            <button
              className="notification-button"
              type="button"
              aria-haspopup="dialog"
              aria-expanded={preferencesOpen}
              aria-label={preferenceCopy[locale].title}
              onClick={() => setPreferencesOpen(true)}
            >
              <HeaderActionGlyph icon="language" />
            </button>
          </div>
        </div>
      </header>

      <main
        key={`${route}-${locationVersion}`}
        id="main-content"
        className={`page-container route-page route-page--${route}`}
      >
        {route === "home" && (
          <>
            <div className="hero">
              <div className="hero__copy">
                <PageTitle
                  title={translate(locale, "brand")
                    .split(/(Link)/)
                    .map((part, index) => (part === "Link" ? <LinkWordmark key={index} /> : part))}
                />
              </div>
            </div>
            {resolvedHome ?? (
              <Card className="home-action-card">
                <div>
                  <p className="eyebrow">{workspaceLabel}</p>
                  <h2>{translate(locale, "openWorkspace")}</h2>
                </div>
                <a
                  className="button"
                  href="/services"
                  onClick={(event) => navigate("services", event)}
                >
                  {workspaceLabel}
                </a>
              </Card>
            )}
          </>
        )}

        {route === "services" && (
          <section className="route-content" aria-labelledby="services-page-title">
            <div className="route-heading">
              <h1 id="services-page-title">{workspaceLabel}</h1>
            </div>
            <div className="app-content">{resolvedFeature}</div>
          </section>
        )}

        {route === "search" && (
          <section className="route-content search-route" aria-labelledby="search-page-title">
            <div className="route-heading">
              <h1 id="search-page-title">{translate(locale, "search")}</h1>
            </div>
            <div className="app-content">{resolvedSearch}</div>
          </section>
        )}

        {route === "notifications" && (
          <section className="route-content" aria-labelledby="notifications-page-title">
            <div className="route-heading">
              <h1 id="notifications-page-title">{translate(locale, "notifications")}</h1>
            </div>
            <div className="notification-page">
              {isValidElement(resolvedNotifications)
                ? cloneElement(resolvedNotifications as ReactElement<{ embedded?: boolean }>, {
                    embedded: true,
                  })
                : resolvedNotifications}
            </div>
          </section>
        )}

        {route === "events" && (
          <section className="route-content" aria-labelledby="events-page-title">
            <div className="route-heading">
              <h1 id="events-page-title">{translate(locale, "events")}</h1>
            </div>
            <div className="event-page">{resolvedEvents ?? <DefaultEvents locale={locale} />}</div>
          </section>
        )}

        {route === "dashboard" && (
          <ManagementRoute
            locale={locale}
            title={translate(locale, "dashboard")}
            content={
              scopedStaff ? (
                <StaffWorkspace capabilities={capabilities} />
              ) : config.id === "taxi" && _dashboardContent ? (
                typeof _dashboardContent === "function" ? (
                  _dashboardContent(locale)
                ) : (
                  _dashboardContent
                )
              ) : (
                <WorkspaceDashboard appId={config.id} />
              )
            }
            hideHeading={contentOwnsHeading.includes("dashboard")}
          />
        )}
        {route === "my-services" && (
          <ManagementRoute
            locale={locale}
            title={resolvedMyServicesLabel}
            content={config.id === "delivery-owner" ? <ManagedRiders /> : resolvedMyServices}
            hideHeading={contentOwnsHeading.includes("my-services")}
          />
        )}
        {route === "orders" && (
          <ManagementRoute
            locale={locale}
            title={ordersLabel}
            content={
              <>
                {!new URLSearchParams(window.location.search).has("record") &&
                  !(
                    config.id === "customer" &&
                    new URLSearchParams(window.location.search).get("tab") === "requests"
                  ) && <WorkspaceOrderCounts appId={config.id} />}
                {config.id === "customer" &&
                new URLSearchParams(window.location.search).has("support") ? (
                  <ServiceCare mode="customer" orderOnly />
                ) : (
                  resolvedOrders
                )}
              </>
            }
            hideHeading={contentOwnsHeading.includes("orders")}
          />
        )}
        {route === "finance" && (
          <ManagementRoute
            locale={locale}
            title={resolvedFinanceLabel}
            content={resolvedFinance}
            hideHeading={contentOwnsHeading.includes("finance")}
          />
        )}

        {route === "more" && (
          <section className="route-content" aria-labelledby="more-page-title">
            {!["/more/how-to-use", "/more/how-to-use-taxi"].includes(window.location.pathname) &&
              !contentOwnsHeading.includes("more") &&
              !moreContentOwnsHeadingPaths.includes(
                window.location.pathname.replace(/\/+$/, ""),
              ) && (
                <div className="route-heading">
                  <h1 id="more-page-title">{morePageTitle}</h1>
                </div>
              )}
            {scopedStaff && window.location.pathname === "/more" ? (
              <StaffWorkspace capabilities={capabilities} />
            ) : config.id === "customer" &&
              ["/more/favourites", "/more/reviews"].includes(window.location.pathname) ? (
              <CustomerSavedRecords reviews={window.location.pathname.endsWith("reviews")} />
            ) : window.location.pathname === "/more/riders" && config.id === "delivery-owner" ? (
              <ManagedRiders />
            ) : ["/more/rooms", "/more/products", "/more/taxi-data", "/more/rider-data"].includes(
                window.location.pathname,
              ) ? (
              resolvedMyServices
            ) : window.location.pathname === "/more/categories" && config.id !== "admin" ? (
              <TaxonomySettings provider />
            ) : ["/more/how-to-use", "/more/how-to-use-taxi"].includes(window.location.pathname) ? (
              <AppManual role={config.id} />
            ) : ["/more/support", "/more/legal", "/more/safety"].includes(
                window.location.pathname,
              ) && config.id !== "admin" ? (
              <ManagedContent
                section={window.location.pathname.split("/").pop()!}
                locale={locale}
                fallback={<Card>Contact platform support for assistance.</Card>}
              />
            ) : window.location.pathname === "/more/provider-subscription" &&
              config.id === "delivery-rider" ? (
              <Card>
                <h2>Subscription</h2>
                <p>Your rider account is covered by your delivery owner’s subscription.</p>
              </Card>
            ) : window.location.pathname === "/more" && ownerNavigation && config.id !== "admin" ? (
              <div className="more-menu-list">
                {[
                  ["profile", "Profile"],
                  ...(config.id === "hotel"
                    ? [["rooms", "Rooms"]]
                    : config.id === "merchant"
                      ? [["products", "Products"]]
                      : config.id === "delivery-owner"
                        ? [["riders", "Rider accounts"]]
                        : config.id === "taxi"
                          ? [["taxi-data", "Taxi data"]]
                          : []),
                  ["provider-subscription", "Subscription"],
                  ...(config.id !== "delivery-rider"
                    ? [
                        ["provider-events", "Event participation"],
                        ["provider-ads", "Promotions"],
                        ["provider-settings", "Opening hours & availability"],
                        ["categories", "Categories & tags"],
                      ]
                    : []),
                  ["service-help", "Reviews & order support"],
                  ["how-to-use", "How to use Link"],
                ].map(([path, label]) => (
                  <a className="more-menu-row" key={path} href={`/more/${path}`}>
                    <MoreMenuIcon name={path ?? "menu"} />
                    <strong>{label}</strong>
                    <b aria-hidden="true">›</b>
                  </a>
                ))}
              </div>
            ) : window.location.pathname === "/more/service-help" ? (
              <ServiceCare
                mode={
                  config.id === "admin"
                    ? "admin"
                    : config.id === "customer"
                      ? "customer"
                      : "provider"
                }
              />
            ) : [
                "/more/provider-events",
                "/more/provider-subscription",
                "/more/provider-settings",
                "/more/provider-ads",
              ].some(
                (path) =>
                  window.location.pathname === path ||
                  (path === "/more/provider-events" &&
                    window.location.pathname.startsWith(path + "/")),
              ) && ["hotel", "merchant", "delivery-owner", "taxi"].includes(config.id) ? (
              window.location.pathname.startsWith("/more/provider-events") ? (
                <EventsWorkspace mode="provider" />
              ) : window.location.pathname === "/more/provider-subscription" ? (
                <TaxiSubscriptionRequests />
              ) : window.location.pathname === "/more/provider-settings" ? (
                <ProviderSettings
                  ownerRole={
                    (
                      {
                        hotel: "hotel_owner",
                        merchant: "merchant_owner",
                        "delivery-owner": "delivery_owner",
                        taxi: "taxi_driver",
                      } as Record<string, string>
                    )[config.id]
                  }
                />
              ) : (
                <PartnerPromotionRequests
                  providerKind={
                    config.id === "delivery-owner"
                      ? "delivery"
                      : (config.id as "hotel" | "merchant" | "taxi")
                  }
                />
              )
            ) : (
              (resolvedMore ?? (
                <Card className="more-card">
                  <BrandLogo />
                  <p>{translate(locale, "tagline")}</p>
                  <strong>{platformOwner}</strong>
                </Card>
              ))
            )}
            {auth.user && window.location.pathname.replace(/\/+$/, "") === "/more" && (
              <div className="filtered-list-stack">
                {orderActivity.control}
                <SignOutButton onDone={() => window.location.assign("/")} />
              </div>
            )}
          </section>
        )}

        {route === "not-found" && (
          <ErrorPage
            code="404"
            title={translate(locale, "notFoundTitle")}
            description={translate(locale, "notFoundHelp")}
            primaryLabel={translate(locale, "goHome")}
            primaryHref={roleHomePath}
          />
        )}

        {route === "server-error" && (
          <ErrorPage
            code="500"
            title={translate(locale, "serverErrorTitle")}
            description={translate(locale, "serverErrorHelp")}
            primaryLabel={translate(locale, "reload")}
            onPrimary={() => window.location.reload()}
          />
        )}
        <InstallApp
          visible={route === "more" && window.location.pathname.replace(/\/+$/, "") === "/more"}
        />
      </main>

      <nav
        className={`mobile-tabbar ${config.id === "admin" || !ownerNavigation ? "mobile-tabbar--five" : ""}`}
        aria-label={translate(locale, "workspace")}
      >
        {ownerNavigation ? (
          <>
            <MobileTab
              active={route === "dashboard"}
              route="dashboard"
              icon="dashboard"
              label={translate(locale, "dashboard")}
              onNavigate={navigate}
            />
            {resolvedMyServices && config.id === "admin" && (
              <MobileTab
                active={route === "my-services"}
                route="my-services"
                href={myServicesPath}
                icon="briefcase"
                label={resolvedMyServicesLabel}
                onNavigate={navigate}
              />
            )}
            <MobileTab
              active={route === "orders"}
              route="orders"
              icon="orders"
              label={ordersLabel}
              onNavigate={navigate}
            />
            {resolvedFinance && (
              <MobileTab
                active={route === "finance"}
                route="finance"
                icon="wallet"
                label={resolvedFinanceLabel}
                onNavigate={navigate}
              />
            )}
            <MobileTab
              active={route === "more"}
              route="more"
              icon="more"
              label={translate(locale, "more")}
              onNavigate={navigate}
            />
          </>
        ) : (
          <>
            <MobileTab
              active={route === "home"}
              route="home"
              icon="home"
              label={translate(locale, "home")}
              onNavigate={navigate}
            />
            <MobileTab
              active={route === "services"}
              route="services"
              icon="grid"
              label={workspaceLabel}
              onNavigate={navigate}
            />
            <MobileTab
              active={route === "events"}
              route="events"
              icon="calendar"
              label={translate(locale, "events")}
              onNavigate={navigate}
            />
            {resolvedOrders && (
              <MobileTab
                active={route === "orders"}
                route="orders"
                icon="orders"
                label={ordersLabel}
                onNavigate={navigate}
              />
            )}
            <MobileTab
              active={route === "more"}
              route="more"
              icon="more"
              label={translate(locale, "more")}
              onNavigate={navigate}
            />
          </>
        )}
      </nav>

      {!auth.user && (
        <footer className="footer">
          <div className="page-container footer__inner">
            <div className="footer__brand">
              <BrandLogo />
              <p>{translate(locale, "tagline")}</p>
            </div>
            <nav className="footer__column" aria-label={footer.company}>
              <h2>{footer.company}</h2>
              <a href="/more?info=about" onClick={(event) => openInformation("about", event)}>
                {footer.about}
              </a>
              <a href="/more?info=support" onClick={(event) => openInformation("support", event)}>
                {footer.contact}
              </a>
              <a href="tel:+959798392286">
                {footer.hotline}: <span className="footer__phone">+959798392286</span>
              </a>
            </nav>
            <nav className="footer__column" aria-label={footer.legal}>
              <h2>{footer.legal}</h2>
              <a href="/more?info=terms" onClick={(event) => openInformation("terms", event)}>
                {footer.terms}
              </a>
              <a href="/more?info=privacy" onClick={(event) => openInformation("privacy", event)}>
                {footer.privacy}
              </a>
              <a
                href="/more?info=cancellation"
                onClick={(event) => openInformation("cancellation", event)}
              >
                {footer.cancellation}
              </a>
            </nav>
            <div className="footer__bottom">
              <span>
                © {new Date().getFullYear()} {platformOwner}
              </span>
              <span>{footer.community}</span>
            </div>
          </div>
        </footer>
      )}

      {preferencesOpen && (
        <PreferencesDialog
          locale={locale}
          onLocaleChange={setLocale}
          onClose={() => setPreferencesOpen(false)}
        />
      )}
      {orderActivity.popup}
      {selectedInformation &&
        informationContent?.(locale, selectedInformation, () => setSelectedInformation(null))}
    </div>
  );
}

function PreferencesDialog({
  locale,
  onLocaleChange,
  onClose,
}: {
  readonly locale: Locale;
  readonly onLocaleChange: (locale: Locale) => void;
  readonly onClose: () => void;
}) {
  const text = preferenceCopy[locale];
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const trigger = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "Tab") {
        const buttons = dialogRef.current?.querySelectorAll<HTMLButtonElement>("button");
        const first = buttons?.[0];
        const last = buttons?.[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", close);
      trigger?.focus();
    };
  }, [onClose]);

  return (
    <div className="preferences-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="preferences-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="preferences-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="preferences-dialog__header">
          <div>
            <h2 id="preferences-title">{text.title}</h2>
          </div>
          <button
            className="service-dialog__close"
            type="button"
            aria-label={text.close}
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <fieldset className="preference-group">
          <legend>{text.language}</legend>
          <div className="preference-options preference-options--language">
            {supportedLocales.map((supportedLocale) => (
              <button
                className={`preference-option ${locale === supportedLocale ? "preference-option--active" : ""}`}
                type="button"
                key={supportedLocale}
                aria-pressed={locale === supportedLocale}
                onClick={() => {
                  onLocaleChange(supportedLocale);
                  onClose();
                }}
              >
                <span>{localeLabels[supportedLocale]}</span>
                {locale === supportedLocale && <b aria-hidden="true">✓</b>}
              </button>
            ))}
          </div>
        </fieldset>
      </section>
    </div>
  );
}

function DefaultEvents({ locale }: { readonly locale: Locale }) {
  const text = defaultEventCopy[locale];
  return (
    <div className="customer-events">
      <p className="event-intro">{text.intro}</p>
      <div className="event-coming-card">
        <span aria-hidden="true">✦</span>
        <div>
          <strong>{text.title}</strong>
          <p>{text.description}</p>
        </div>
      </div>
    </div>
  );
}

function ManagementRoute({
  locale,
  title,
  content,
  hideHeading = false,
}: {
  readonly locale: Locale;
  readonly title: string;
  readonly content?: ReactNode;
  readonly hideHeading?: boolean;
}) {
  const auth = useAuth();
  return (
    <section className="route-content" aria-label={title}>
      {!hideHeading && (
        <div className="route-heading">
          <h1>{title}</h1>
        </div>
      )}
      <div className="app-content">
        {auth.loading ? (
          <Card className="protected-state protected-state--loading">
            <span className="protected-state__spinner" aria-hidden="true" />
            <strong>{translate(locale, "authLoading")}</strong>
          </Card>
        ) : !auth.user ? (
          <ProtectedLogin locale={locale} />
        ) : (
          (content ?? (
            <Card className="empty-state">
              <strong>No records available.</strong>
            </Card>
          ))
        )}
      </div>
    </section>
  );
}

function AuthenticationScreen({
  config,
  locale,
  onLocaleChange,
  children,
}: {
  readonly config: AppConfig;
  readonly locale: Locale;
  readonly onLocaleChange: (locale: Locale) => void;
  readonly children: ReactNode;
}) {
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  return (
    <div
      className="app-shell authentication-shell"
      data-app={config.id}
      style={
        { "--accent": config.accent, "--accent-text": config.accentText } as React.CSSProperties
      }
    >
      <main className="authentication-page">
        <header className="authentication-header">
          <div className="authentication-brand">
            <BrandLogo />
            <span>{translateApp(locale, config.id, "role", config.roleLabel)}</span>
          </div>
          <button
            className="notification-button authentication-language"
            type="button"
            aria-label={preferenceCopy[locale].title}
            aria-haspopup="dialog"
            aria-expanded={preferencesOpen}
            onClick={() => setPreferencesOpen(true)}
          >
            <HeaderActionGlyph icon="language" />
          </button>
        </header>
        {children}
        <p className="authentication-owner">{platformOwner}</p>
      </main>
      {preferencesOpen && (
        <PreferencesDialog
          locale={locale}
          onLocaleChange={onLocaleChange}
          onClose={() => setPreferencesOpen(false)}
        />
      )}
    </div>
  );
}

function PasswordRecovery({
  locale,
  reset = false,
  onBack,
}: {
  locale: Locale;
  reset?: boolean;
  onBack?: () => void;
}) {
  const auth = useAuth();
  const [token] = useState(
    () => new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "",
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useFeedback();
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (reset) window.history.replaceState({}, "", window.location.pathname);
  }, [reset]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    if (reset && values.get("password") !== values.get("confirmation")) {
      setMessage("Passwords do not match.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      setMessage(
        reset
          ? await auth.resetPassword(token, String(values.get("password")))
          : await auth.requestRecovery(String(values.get("email"))),
      );
      setDone(true);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to complete recovery.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card className="protected-login password-recovery">
      <h2>
        {reset ? translate(locale, "resetYourPassword") : translate(locale, "forgotPassword")}
      </h2>
      {!done && (
        <form onSubmit={(event) => void submit(event)}>
          {reset ? (
            <>
              <label className="field">
                <span>New password</span>
                <input
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={72}
                  required
                />
              </label>
              <label className="field">
                <span>Confirm password</span>
                <input
                  name="confirmation"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={72}
                  required
                />
              </label>
            </>
          ) : (
            <label className="field">
              <span>Account email</span>
              <input name="email" type="email" autoComplete="email" maxLength={254} required />
            </label>
          )}
          <button className="button" disabled={busy}>
            {busy
              ? "Please wait…"
              : reset
                ? translate(locale, "resetYourPassword")
                : translate(locale, "sendRecoveryEmail")}
          </button>
        </form>
      )}
      {message && <p role="status">{message}</p>}
      {onBack ? (
        <button type="button" className="button button--secondary" onClick={onBack}>
          Back to sign in
        </button>
      ) : (
        <a className="button button--secondary" href="/">
          Back to sign in
        </a>
      )}
    </Card>
  );
}

function ProtectedLogin({
  locale,
  allowRegistration = false,
}: {
  readonly locale: Locale;
  readonly allowRegistration?: boolean;
}) {
  const auth = useAuth();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useFeedback();
  const [registering, setRegistering] = useState(false);
  const [recovering, setRecovering] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const form = new FormData(event.currentTarget);
    const error = registering
      ? await auth.signUp(
          String(form.get("identifier")),
          String(form.get("password")),
          String(form.get("name")),
          String(form.get("username")),
        )
      : await auth.signIn(String(form.get("identifier")), String(form.get("password")));
    if (error) {
      setMessage(error);
    } else if (registering) {
      window.history.replaceState({}, "", "/");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
    setBusy(false);
  }

  if (recovering) return <PasswordRecovery locale={locale} onBack={() => setRecovering(false)} />;
  return (
    <Card className="protected-login">
      <div className="protected-login__intro">
        <span className="protected-login__icon" aria-hidden="true">
          🔒
        </span>
        <div>
          <p className="eyebrow">
            TaungGyi <LinkWordmark />
          </p>
          <h2>{translate(locale, registering ? "createAccount" : "signInRequired")}</h2>
        </div>
      </div>
      {auth.configured ? (
        <form className="form-grid" onSubmit={(event) => void submit(event)}>
          {registering && (
            <>
              <label className="field">
                <span>{translate(locale, "name")}</span>
                <input name="name" type="text" autoComplete="name" minLength={2} required />
              </label>
              <label className="field">
                <span>{translate(locale, "username")}</span>
                <input
                  name="username"
                  type="text"
                  autoComplete="username"
                  minLength={3}
                  maxLength={30}
                  pattern="[A-Za-z0-9_]+"
                  required
                />
              </label>
            </>
          )}
          <label className="field">
            <span>
              {translate(locale, registering ? "registrationContact" : "loginIdentifier")}
            </span>
            <input
              name="identifier"
              type="text"
              autoComplete={registering ? "email" : "username"}
              required
            />
            {registering && <small>{translate(locale, "phoneHelp")}</small>}
          </label>
          <label className="field">
            <span>{translate(locale, "password")}</span>
            <input
              name="password"
              type="password"
              autoComplete={registering ? "new-password" : "current-password"}
              required
            />
          </label>
          <button className="button" disabled={busy}>
            {translate(
              locale,
              busy
                ? registering
                  ? "creatingAccount"
                  : "signingIn"
                : registering
                  ? "createAccount"
                  : "signIn",
            )}
          </button>
          {!registering && (
            <button type="button" className="text-button" onClick={() => setRecovering(true)}>
              {translate(locale, "forgotPassword")}
            </button>
          )}
          {allowRegistration && (
            <button
              className="protected-login__switch"
              type="button"
              disabled={busy}
              onClick={() => {
                setRegistering((value) => !value);
                setMessage("");
              }}
            >
              {translate(locale, registering ? "haveAccount" : "needAccount")}
            </button>
          )}
          {message && (
            <p className="form-message" role="alert">
              {message}
            </p>
          )}
        </form>
      ) : (
        <p className="form-message" role="alert">
          {translate(locale, "authUnavailable")}
        </p>
      )}
    </Card>
  );
}

function ErrorPage({
  code,
  title,
  description,
  primaryLabel,
  primaryHref,
  onPrimary,
}: {
  readonly code: string;
  readonly title: string;
  readonly description: string;
  readonly primaryLabel: string;
  readonly primaryHref?: string;
  readonly onPrimary?: () => void;
}) {
  return (
    <section className="error-page" aria-labelledby={`error-${code}-title`}>
      <Card className="error-card">
        <BrandLogo />
        <span className="error-card__code">{code}</span>
        <h1 id={`error-${code}-title`}>{title}</h1>
        <p>{description}</p>
        {primaryHref ? (
          <a className="button" href={primaryHref}>
            {primaryLabel}
          </a>
        ) : (
          <button className="button" type="button" onClick={onPrimary}>
            {primaryLabel}
          </button>
        )}
      </Card>
    </section>
  );
}

class PlatformErrorBoundary extends Component<
  { readonly config: AppConfig; readonly children: ReactNode },
  { readonly failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    const savedLocale = (localStorage.getItem("taunggyi-link-locale") ??
      localStorage.getItem("tg-plus-locale")) as Locale | null;
    const locale = savedLocale && supportedLocales.includes(savedLocale) ? savedLocale : "en";
    return (
      <div
        className="app-shell error-shell"
        data-app={this.props.config.id}
        style={
          {
            "--accent": this.props.config.accent,
            "--accent-text": this.props.config.accentText,
          } as React.CSSProperties
        }
      >
        <main className="page-container">
          <ErrorPage
            code="500"
            title={translate(locale, "serverErrorTitle")}
            description={translate(locale, "serverErrorHelp")}
            primaryLabel={translate(locale, "reload")}
            onPrimary={() => window.location.reload()}
          />
        </main>
      </div>
    );
  }
}

type MobileTabIcon =
  "home" | "grid" | "bell" | "calendar" | "dashboard" | "briefcase" | "orders" | "wallet" | "more";

function HeaderActionGlyph({ icon }: { readonly icon: "search" | "language" }) {
  if (icon === "language") return <GlobeIcon />;
  return (
    <svg className="header-action-glyph" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.5 15.5 5 5" />
    </svg>
  );
}

function DesktopLink({
  active,
  route,
  href,
  label,
  onNavigate,
}: {
  readonly active: boolean;
  readonly route: AppRoute;
  readonly href?: string;
  readonly label: string;
  readonly onNavigate: (route: AppRoute, event: MouseEvent<HTMLAnchorElement>) => void;
}) {
  return (
    <a
      className={`desktop-nav__link ${active ? "desktop-nav__link--active" : ""}`}
      href={href ?? routePaths[route]}
      aria-current={active ? "page" : undefined}
      onClick={(event) => onNavigate(route, event)}
    >
      {label}
    </a>
  );
}

function MobileTab({
  active,
  route,
  href,
  icon,
  label,
  onNavigate,
}: {
  readonly active: boolean;
  readonly route: AppRoute;
  readonly href?: string;
  readonly icon: MobileTabIcon;
  readonly label: string;
  readonly onNavigate: (route: AppRoute, event: MouseEvent<HTMLAnchorElement>) => void;
}) {
  return (
    <a
      className={`mobile-tab ${active ? "mobile-tab--active" : ""}`}
      href={href ?? routePaths[route]}
      aria-current={active ? "page" : undefined}
      onClick={(event) => onNavigate(route, event)}
    >
      <MobileTabGlyph icon={icon} />
      <span>{label}</span>
    </a>
  );
}

function MobileTabGlyph({ icon }: { readonly icon: MobileTabIcon }) {
  const paths: Record<MobileTabIcon, ReactNode> = {
    home: (
      <path d="M3.5 10.5 12 3l8.5 7.5v9a1.5 1.5 0 0 1-1.5 1.5h-4.5v-6h-5v6H5a1.5 1.5 0 0 1-1.5-1.5z" />
    ),
    grid: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="2" />
        <rect x="14" y="3" width="7" height="7" rx="2" />
        <rect x="3" y="14" width="7" height="7" rx="2" />
        <rect x="14" y="14" width="7" height="7" rx="2" />
      </>
    ),
    bell: <path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />,
    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="3" />
        <path d="M8 3v4m8-4v4M3 10h18M8 14h.1m4 0h.1m4 0h.1M8 18h.1m4 0h.1" />
      </>
    ),
    dashboard: <path d="M4 13h6V4H4zm10 7h6v-9h-6zM4 20h6v-3H4zm10-13h6V4h-6z" />,
    briefcase: (
      <>
        <rect x="3" y="7" width="18" height="13" rx="3" />
        <path d="M8 7V4h8v3M3 12h18M10 12v2h4v-2" />
      </>
    ),
    orders: (
      <>
        <path d="M6 3h12v18H6zM9 8h6M9 12h6M9 16h4" />
        <path d="M4 6H3v12h1" />
      </>
    ),
    wallet: (
      <>
        <path d="M3 6h16a2 2 0 0 1 2 2v11H5a2 2 0 0 1-2-2z" />
        <path d="M3 8V6a2 2 0 0 1 2-2h13v4M15 12h6v4h-6a2 2 0 0 1 0-4Z" />
      </>
    ),
    more: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
      </>
    ),
  };
  return (
    <svg className="mobile-tab__icon" viewBox="0 0 24 24" aria-hidden="true">
      {paths[icon]}
    </svg>
  );
}
